import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PRIVACY_ROLES, REGISTER_EDIT_ROLES, type CatalogueKey, type HouseDetail, type Member } from '@mahallu/shared';
import { loadErrorKey, useHouse, useMe } from '../api';
import { Icon } from '../components/Icon';
import { Badge } from '../components/Parts';
import { inr } from '../format';
import { ConsentTab } from './HouseConsent';
import { HouseAccountTab } from './HouseAccountTab';
import { ErasureRequestForm } from './PrivacyRequests';
import { HouseEditForm, MemberForm } from './RegisterForms';

const TABS: { key: string; label: CatalogueKey }[] = [
  { key: 'overview', label: 'tab.overview' },
  { key: 'members', label: 'tab.members' },
  { key: 'residence', label: 'tab.residence' },
  { key: 'consent', label: 'tab.consent' },
  { key: 'varisankhya', label: 'tab.varisankhya' },
  { key: 'certificates', label: 'tab.certificates' },
  { key: 'madrasa', label: 'tab.madrasa' },
  { key: 'documents', label: 'tab.documents' },
  { key: 'notes', label: 'tab.notes' },
  { key: 'history', label: 'tab.history' },
];

export function HouseDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const ml = i18n.language === 'ml';
  const [tab, setTab] = useState('overview');
  const house = useHouse(id);
  const me = useMe();
  const canEdit = Boolean(me.data?.role && REGISTER_EDIT_ROLES.includes(me.data.role));
  const canPrivacy = Boolean(me.data?.role && PRIVACY_ROLES.includes(me.data.role));
  const [form, setForm] = useState<{ kind: 'house' } | { kind: 'member'; member?: Member } | { kind: 'erasure'; member: Member } | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.modal-scrim')) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const h = house.data;
  return (
    <div className="scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="drawer" role="dialog" aria-modal="true" aria-label={h ? h.nameEn : t('drawer.loading')}>
        <div className="drawer-h">
          <div className="mod-icon">
            <Icon name="home" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3>
              {h?.nameEn ?? t('drawer.loading')} {h?.nameMl ? <span className="ml note">{h.nameMl}</span> : null}
            </h3>
            {h ? (
              <p className="num">
                {h.houseNo} · {ml && h.ward.nameMl ? h.ward.nameMl : h.ward.nameEn} · {t('drawer.memberCount', { n: h.memberCount })} ·{' '}
                <Badge tone={h.status === 'ACTIVE' ? 'ok' : 'neutral'}>{t(`status.${h.status}`)}</Badge>
              </p>
            ) : null}
          </div>
          <button ref={closeRef} type="button" className="icon-btn" title={t('drawer.close')} aria-label={t('drawer.close')} onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="tabs" role="tablist">
          {TABS.map((tb) => (
            <button key={tb.key} type="button" role="tab" aria-selected={tab === tb.key} onClick={() => setTab(tb.key)}>
              {t(tb.label)}
            </button>
          ))}
        </div>
        <div className="drawer-b" role="tabpanel">
          {house.isError ? <p className="note">{t(loadErrorKey(house.error))}</p> : !h ? <p className="note">{t('drawer.loading')}</p> : <TabBody tab={tab} h={h} ml={ml} canEdit={canEdit} canPrivacy={canPrivacy} onEditHouse={() => setForm({ kind: 'house' })} onMember={(member) => setForm({ kind: 'member', member })} onErasure={(member) => setForm({ kind: 'erasure', member })} />}
        </div>
      </div>
      {h && form?.kind === 'house' ? <HouseEditForm house={h} onClose={() => setForm(null)} /> : null}
      {h && form?.kind === 'member' ? <MemberForm houseId={h.id} member={form.member} onClose={() => setForm(null)} /> : null}
      {form?.kind === 'erasure' ? <ErasureRequestForm member={form.member} onClose={() => setForm(null)} /> : null}
    </div>
  );
}

function TabBody(p: { tab: string; h: HouseDetail; ml: boolean; canEdit: boolean; canPrivacy: boolean; onEditHouse: () => void; onMember: (m?: Member) => void; onErasure: (m: Member) => void }) {
  const { tab, h, ml, canEdit, onEditHouse, onMember } = p;
  const { t } = useTranslation();
  const alive = h.members.filter((m) => m.status === 'ALIVE');

  if (tab === 'overview') {
    const head = alive.find((m) => m.relation === 'HEAD');
    const rows: [CatalogueKey, string][] = [
      ['field.houseNo', h.houseNo],
      ['field.ward', ml && h.ward.nameMl ? h.ward.nameMl : h.ward.nameEn],
      ['field.nameEn', h.nameEn],
      ['field.nameMl', h.nameMl ?? t('common.notGiven')],
      ['field.head', head?.nameEn ?? t('common.notGiven')],
      ['field.ownership', h.ownership ? t(`ownership.${h.ownership}`) : t('common.notGiven')],
      ['field.plan', `${t(`plan.${h.ratePlan}`)}${h.monthlyRateInr ? ` · ${inr(h.monthlyRateInr)}${t('unit.perMonth')}` : ''}`],
      ['field.consent', t(`consent.${h.consent}`)],
    ];
    return (
      <>
        {canEdit ? (
          <div className="actions">
            <button type="button" className="btn sm" onClick={onEditHouse}>
              <Icon name="edit" />
              {t('house.edit')}
            </button>
          </div>
        ) : null}
        <dl className="kv">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt>{t(k)}</dt>
              <dd className={k === 'field.nameMl' ? 'ml' : undefined}>{v}</dd>
            </div>
          ))}
        </dl>
        <div className="callout">{t('drawer.arrearsNote')}</div>
      </>
    );
  }

  if (tab === 'members') {
    return (
      <>
        {canEdit ? (
          <div className="actions">
            <button type="button" className="btn sm primary" onClick={() => onMember()}>
              <Icon name="plus" />
              {t('member.add')}
            </button>
          </div>
        ) : null}
        <div className="tbl-wrap">
          <div className="tbl-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t('field.name')}</th>
                  <th>{t('field.relation')}</th>
                  <th>{t('field.born')}</th>
                  <th>{t('field.lives')}</th>
                  <th>{t('field.phone')}</th>
                  {canEdit || p.canPrivacy ? <th>{t('col.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {h.members.map((m) => (
                  <tr key={m.id} className={m.status === 'ALIVE' ? undefined : 'muted-row'}>
                    <td>
                      <b>{m.nameEn}</b>
                      {m.nameMl ? <span className="ml note"> {m.nameMl}</span> : null}
                      {m.status !== 'ALIVE' ? (
                        <>
                          {' '}
                          <Badge tone="neutral">{t(`mstatus.${m.status}`)}</Badge>
                        </>
                      ) : null}
                      {m.detailsErasedAt ? (
                        <>
                          {' '}
                          <Badge tone="neutral">{t('member.erased')}</Badge>
                        </>
                      ) : null}
                    </td>
                    <td>{t(`rel.${m.relation}`)}</td>
                    <td className="num">{m.birthYear ?? '—'}</td>
                    <td>
                      {m.residence === 'RESIDENT' ? (
                        <Badge tone="info">{t('res.RESIDENT')}</Badge>
                      ) : (
                        <Badge tone={m.residence === 'ABROAD' ? 'violet' : 'teal'}>
                          {t(`res.${m.residence}`)}
                          {m.city ? ` · ${m.city}` : ''}
                        </Badge>
                      )}
                    </td>
                    <td className="num">{m.phone ?? '—'}</td>
                    {canEdit || p.canPrivacy ? (
                      <td>
                        {canEdit ? (
                          <button type="button" className="icon-btn" title={t('member.edit')} aria-label={`${t('member.edit')} ${m.nameEn}`} onClick={() => onMember(m)}>
                            <Icon name="edit" />
                          </button>
                        ) : null}
                        {p.canPrivacy ? (
                          <button type="button" className="icon-btn" title={t('member.erasure')} aria-label={`${t('member.erasure')}: ${m.nameEn}`} onClick={() => p.onErasure(m)}>
                            <Icon name="shield" />
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  if (tab === 'residence') {
    const away = alive.filter((m) => m.residence !== 'RESIDENT');
    if (!away.length) return <p className="note">{t('drawer.allResident')}</p>;
    return (
      <>
        {away.map((m) => (
          <div key={m.id} className="card">
            <div className="card-h">
              <b>{m.nameEn}</b>
              <Badge tone={m.residence === 'ABROAD' ? 'violet' : 'teal'}>{t(`res.${m.residence}`)}</Badge>
            </div>
            <dl className="kv">
              <div>
                <dt>{t('field.country')}</dt>
                <dd>{m.country ?? t('common.notGiven')}</dd>
              </div>
              <div>
                <dt>{t('field.city')}</dt>
                <dd>{m.city ?? t('common.notGiven')}</dd>
              </div>
              <div>
                <dt>{t('field.work')}</dt>
                <dd>{m.work ?? t('common.notGiven')}</dd>
              </div>
              <div>
                <dt>{t('field.phone')}</dt>
                <dd className="num">{m.phone ?? t('common.notGiven')}</dd>
              </div>
            </dl>
          </div>
        ))}
      </>
    );
  }

  if (tab === 'consent') return <ConsentTab house={h} canEdit={canEdit} />;
  if (tab === 'varisankhya') return <HouseAccountTab house={h} />;

  return <p className="note">{t('drawer.comingLater')}</p>;
}
